import { FC, useEffect } from "react";
import {
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { resolveGroupSlots } from "../../../openapi/api-client";
import { ResolvedTeamReference } from "../../../Components/ResolvedTeamReference";
import { teamRefToTeamId } from "../../../utils/teamRefToTeamId";
import { components } from "../../../openapi/generated-types";

type CompetitionGroup = components["schemas"]["CompetitionGroup"];

interface ResolveSlotsModalProps {
  competitionId: string;
  groupId: string;
  group: CompetitionGroup | undefined;
  enrolledTeams: components["schemas"]["Team"][];
  onClose: () => void;
  onSuccess: () => void;
}

export const ResolveSlotsModal: FC<ResolveSlotsModalProps> = ({
  competitionId,
  groupId,
  group,
  enrolledTeams,
  onClose,
  onSuccess,
}) => {
  const slots = group?.slots ?? [];

  const form = useForm<{ teamIds: (string | null)[] }>({
    initialValues: {
      teamIds: slots.map(() => null),
    },
  });

  const recommendedTeamIds = useQuery({
    queryKey: ["resolveSlots", groupId, slots],
    queryFn: async () => {
      const results: (string | undefined)[] = [];
      for (const slot of slots) {
        const teamId = await teamRefToTeamId(slot, competitionId);
        results.push(teamId);
      }
      return results;
    },
    enabled: slots.length > 0,
  });

  useEffect(() => {
    if (!recommendedTeamIds.isSuccess) {
      return;
    }

    const newTeamIds = recommendedTeamIds.data.map((id) => id ?? null);
    const allNull = form.values.teamIds.every((id) => id === null);
    if (allNull) {
      form.setFieldValue("teamIds", newTeamIds);
    }
  }, [recommendedTeamIds.data, recommendedTeamIds.isSuccess]);

  const resolveSlotsMutation = useMutation({
    mutationFn: resolveGroupSlots,
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    const teamIds = values.teamIds.filter(
      (id): id is string => id !== null && id !== "",
    );

    if (teamIds.length !== slots.length) {
      return;
    }

    await resolveSlotsMutation.mutateAsync({
      id: competitionId,
      groupId,
      teamIds,
    });
  });

  const allSlotsResolved =
    form.values.teamIds.length === slots.length &&
    form.values.teamIds.every((id) => id !== null && id !== "");

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={`Slots auflösen: ${group?.name ?? "Gruppe"}`}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          {!recommendedTeamIds.isSuccess ? (
            <Group justify="center" p="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Lade Vorschläge...
              </Text>
            </Group>
          ) : (
            slots.map((slot, index) => (
              <Select
                key={index}
                label={`Slot ${index + 1}`}
                description={
                  <ResolvedTeamReference
                    teamReference={slot}
                    competitionId={competitionId}
                  />
                }
                placeholder="Team auswählen"
                searchable
                clearable
                nothingFoundMessage="Kein passendes Team gefunden"
                data={enrolledTeams.map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
                value={form.values.teamIds[index] ?? ""}
                onChange={(value) => {
                  const newTeamIds = [...form.values.teamIds];
                  newTeamIds[index] = value;
                  form.setFieldValue("teamIds", newTeamIds);
                }}
              />
            ))
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={resolveSlotsMutation.isPending}
              disabled={!allSlotsResolved}
            >
              Slots auflösen
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
